import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
    {
        title: "1. Quem somos",
        body: [
            "Este site é operado pela Buzas Indústria e Comércio Ltda (“Buzas”), com sede na Rua Serra Redonda, 450 — Vila Industrial — São Paulo — SP, CEP 03244-150, que atua como controladora dos dados pessoais tratados aqui, nos termos da Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais — LGPD).",
        ],
    },
    {
        title: "2. Dados que coletamos",
        body: [
            "Dados que você nos fornece: nome, empresa, telefone, e-mail e as informações que escrever na mensagem do formulário de orçamento, além dos dados que compartilhar ao falar conosco por WhatsApp, telefone ou e-mail.",
            "Dados de navegação: mediante o seu consentimento, utilizamos cookies e ferramentas de análise que registram como você interage com as páginas (páginas visitadas, cliques, rolagem, tipo de dispositivo e navegador, endereço IP e origem do acesso).",
        ],
    },
    {
        title: "3. Para que usamos os dados",
        body: [
            "Responder às suas solicitações de orçamento e de contato, e dar andamento às tratativas comerciais e técnicas.",
            "Enviar comunicações sobre nossos produtos e serviços, das quais você pode se descadastrar a qualquer momento.",
            "Entender como o site é utilizado, para corrigir falhas e melhorar o conteúdo e a experiência de navegação.",
            "Cumprir obrigações legais e regulatórias e exercer direitos em processos judiciais, administrativos ou arbitrais.",
        ],
    },
    {
        title: "4. Bases legais",
        body: [
            "Tratamos dados pessoais com fundamento nas seguintes hipóteses da LGPD: procedimentos preliminares a contrato, a pedido do titular (art. 7º, V), quando você solicita um orçamento; consentimento (art. 7º, I), para cookies e ferramentas de análise e para comunicações de marketing; legítimo interesse (art. 7º, IX), para o relacionamento comercial e a segurança do site; e cumprimento de obrigação legal ou regulatória (art. 7º, II).",
        ],
    },
    {
        title: "5. Cookies",
        body: [
            "Cookies são pequenos arquivos armazenados no seu navegador. Neste site eles são usados para lembrar sua escolha no aviso de cookies e, se você aceitar, para análise de navegação.",
            "Ao acessar o site pela primeira vez, você pode aceitar ou recusar os cookies de análise. Se recusar, o site continua funcionando normalmente. Você pode rever sua escolha a qualquer momento apagando os dados de navegação deste site no seu navegador.",
        ],
    },
    {
        title: "6. Compartilhamento",
        body: [
            "Não vendemos dados pessoais. Podemos compartilhá-los com prestadores de serviço que nos apoiam na operação do site e no atendimento (hospedagem, análise de navegação, ferramentas de comunicação e de e-mail), sempre limitados ao necessário para a prestação do serviço, e com autoridades públicas, quando exigido por lei.",
            "Alguns desses prestadores podem estar localizados fora do Brasil. Nesses casos, a transferência internacional de dados é feita de acordo com a LGPD.",
        ],
    },
    {
        title: "7. Armazenamento e segurança",
        body: [
            "Os dados são mantidos em serviços confiáveis, com medidas técnicas e administrativas para protegê-los contra acessos não autorizados, perda ou alteração. Eles são conservados pelo tempo necessário para as finalidades descritas nesta política, até que você solicite a exclusão ou enquanto houver obrigação legal de guarda.",
        ],
    },
    {
        title: "8. Seus direitos",
        body: [
            "Nos termos do art. 18 da LGPD, você pode solicitar a qualquer momento: a confirmação da existência de tratamento; o acesso aos seus dados; a correção de dados incompletos, inexatos ou desatualizados; a anonimização, o bloqueio ou a eliminação de dados desnecessários ou excessivos; a portabilidade; a eliminação dos dados tratados com base no consentimento; informações sobre o compartilhamento dos seus dados; e a revogação do consentimento.",
            "Você também tem o direito de apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).",
        ],
    },
    {
        title: "9. Alterações desta política",
        body: [
            "Esta política pode ser atualizada para refletir mudanças no site ou na legislação. A versão vigente estará sempre disponível nesta página, com a data da última atualização.",
        ],
    },
];

export const PrivacyPolicy = () => {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = "Política de Privacidade | Buzas";
        window.scrollTo(0, 0);
        return () => {
            document.title = previousTitle;
        };
    }, []);

    return (
        <main
            data-testid="privacy-policy-page"
            className="min-h-screen flex flex-col bg-white"
        >
            <header className="border-b border-buzas-gray-200">
                <div className="container-buzas flex h-16 md:h-20 items-center justify-between">
                    <Link
                        to="/"
                        data-testid="privacy-logo-link"
                        className="flex items-center gap-2"
                        aria-label="Buzas Laser Service — Início"
                    >
                        <img
                            src="https://customer-assets.emergentagent.com/job_pecas-sob-medida/artifacts/ynnmstrj_logo01.png"
                            alt="Buzas Laser Service"
                            className="h-10 md:h-12 w-auto object-contain"
                        />
                    </Link>
                    <Link
                        to="/"
                        data-testid="privacy-back-link"
                        className="inline-flex items-center gap-2 text-sm font-bold text-buzas-navy hover:text-buzas-red transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Voltar ao site
                    </Link>
                </div>
            </header>

            <article className="container-buzas flex-1 py-14 md:py-20">
                <div className="max-w-3xl">
                    <span className="eyebrow text-buzas-red">LGPD</span>
                    <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-buzas-navy">
                        Política de Privacidade
                    </h1>
                    <p className="mt-3 text-sm text-buzas-gray-500">
                        Última atualização: outubro de 2026
                    </p>
                    <p className="mt-8 text-base text-buzas-ink/80 leading-relaxed">
                        A Buzas respeita a sua privacidade. Esta política
                        explica quais dados pessoais coletamos neste site, como
                        os utilizamos e quais são os seus direitos.
                    </p>

                    {SECTIONS.map((section) => (
                        <section key={section.title} className="mt-10">
                            <h2 className="text-xl md:text-2xl font-bold text-buzas-navy">
                                {section.title}
                            </h2>
                            {section.body.map((paragraph) => (
                                <p
                                    key={paragraph}
                                    className="mt-4 text-base text-buzas-ink/80 leading-relaxed"
                                >
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <section className="mt-10">
                        <h2 className="text-xl md:text-2xl font-bold text-buzas-navy">
                            10. Contato
                        </h2>
                        <p className="mt-4 text-base text-buzas-ink/80 leading-relaxed">
                            Para exercer seus direitos ou tirar dúvidas sobre
                            esta política, escreva para{" "}
                            <a
                                href="mailto:lgpd@buzas.com.br"
                                data-testid="privacy-lgpd-email"
                                className="font-bold text-buzas-navy underline underline-offset-2 hover:text-buzas-red"
                            >
                                lgpd@buzas.com.br
                            </a>
                            . Pedidos de exclusão de dados são atendidos em até
                            5 dias úteis.
                        </p>
                        <p className="mt-4 text-base text-buzas-ink/80 leading-relaxed">
                            Buzas Indústria e Comércio Ltda — Rua Serra
                            Redonda, 450 — Vila Industrial — São Paulo — SP,
                            CEP 03244-150 — +55 11 3385-8700 —{" "}
                            <a
                                href="mailto:comercial@buzas.com.br"
                                className="font-bold text-buzas-navy underline underline-offset-2 hover:text-buzas-red"
                            >
                                comercial@buzas.com.br
                            </a>
                        </p>
                    </section>
                </div>
            </article>

            <footer className="bg-buzas-navy">
                <div className="container-buzas py-6">
                    <p className="text-xs text-white/55">
                        © {new Date().getFullYear()} Buzas — Soluções
                        Industriais. Todos os direitos reservados.
                    </p>
                </div>
            </footer>
        </main>
    );
};
